import React, {Component, ReactNode} from 'react'
import AssocManagementRepository from '../assocDirector/AssocManagementRepository'
import AdminGeneralMeetingRepo, {
    GeneralMeetingFile,
    GeneralMeetingRequest,
    ProposalByAssocRequest,
} from './AdminGeneralMeetingRepo'
import {Information} from '../../network/response/Information'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {Nullable} from '../../app/TypeHelpers'
import {AssocOfCondominium} from '../assocDirector/model/AssocOfCondominium'
import {
    AdminGeneralMeetingInputForm,
    canRegisterGeneralMeeting,
    canSaveAsDraftGeneralMeeting,
    GeneralMeetingFormDataSet,
} from './AdminGeneralMeetingInputForm'
import {PageFotterAreaLeft, PageFotterAreaRight} from '../shared/hoc/withAdminPageTemplate'
import styled from 'styled-components'
import {RightRowFlexContainer, RowFlexContainer} from '../../styledComponents/FlexComponents'
import {AdminDefaultButton, AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {AVAILABLE_OR_NOT, GENERAL_MEETING_STATUS, MESSAGE_TYPE} from '../../CodeMaster'
import {BackLink} from '../../styledComponents/adminPage/Links'
import {NoSelectedValue} from '../../componentHelper/selectHelper'
import {Messages} from '../../Messages'
import Time from '../../dateHelpers/Time'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'

import {NatFileRepo} from '../../__NoAutomatedTests/components/files/repository/NatFileRepo'

interface Props {
    condominiumCd: string
    assocManagementRepo: AssocManagementRepository
    adminGeneralMeetingRepo: AdminGeneralMeetingRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    natFileRepo: NatFileRepo
    eventEmitter: EventEmitterExAdmin
}

interface State {
    assocList: AssocOfCondominium[]
    generalMeetingFormDataSet: GeneralMeetingFormDataSet
    currentDate: Date
}

class AdminGeneralMeetingRegisterPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            assocList: [],
            generalMeetingFormDataSet: {
                generalMeetingName: '',
                selectedAssocIdList: [],
                generalMeetingDate: null,
                startHour: NoSelectedValue,
                startMinute: NoSelectedValue,
                votingPreferenceDeadline: null,
                questionAvailability: AVAILABLE_OR_NOT.AVAILABLE.value,
                questionDeadline: null,
                generalMeetingFileList: [],
                proposalFormDataSetList: [],
                noticeContent: '',
                releaseDate: null
            },
            currentDate: new Date()
        }

        this.handleClickSaveAsDraftButton = this.handleClickSaveAsDraftButton.bind(this)
        this.handleSaveAsDraftOkClick = this.handleSaveAsDraftOkClick.bind(this)
        this.handleRegisterButton = this.handleRegisterButton.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.handleChangeGeneralMeetingFormDataSet = this.handleChangeGeneralMeetingFormDataSet.bind(this)
    }

    static formatDateToString(date: Nullable<Date>): Nullable<string> {
        if (date === null) return null

        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${date.getFullYear()}-${month}-${day}`
    }


    async componentDidMount(): Promise<void> {
        const {
            condominiumCd,
            assocManagementRepo,
            currentDateTimeRepo,
            eventEmitter,
            asyncCallFunction,
            navigate,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const assocList: Nullable<AssocOfCondominium[]> =
                await asyncCallFunction(
                    assocManagementRepo.getAssocList.bind(assocManagementRepo, condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
            if (assocList === null || assocList.length === 0) {
                navigate('/admin/error')
                return
            }
            this.setState({assocList})

            const currentDateTimeResponse = await NatAsyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                navigate,
            )
            if (currentDateTimeResponse !== null && currentDateTimeResponse.details !== null) {
                const currentDate: Date = new Date(currentDateTimeResponse.details.currentDateTime)
                this.setState({currentDate})
            }
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async handleClickSaveAsDraftButton(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.WGMTA0001.errorLevel,
                message: Messages.WGMTA0001.toString(),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleSaveAsDraftOkClick,
                    },
                ],
            },
        )
    }

    async handleRegisterButton(): Promise<void> {
        const releaseDate = this.state.generalMeetingFormDataSet.releaseDate
        if (releaseDate === null) return

        const releaseDateText = `${releaseDate.getFullYear()}年${releaseDate.getMonth() + 1}月${releaseDate.getDate()}日 午前0時頃`

        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.WGMTA0002.errorLevel,
                message: Messages.WGMTA0002.toString(releaseDateText,'午前8時頃'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick,
                    },
                ],
            },
        )
    }

    createGeneralMeetingRequest(generalMeetingFormDataSet: GeneralMeetingFormDataSet, isDraft: boolean)
        : GeneralMeetingRequest {
        let startTime: Nullable<string> = null
        if (generalMeetingFormDataSet.startHour !== NoSelectedValue
            && generalMeetingFormDataSet.startMinute !== NoSelectedValue
        ) {
            startTime = new Time(Number(generalMeetingFormDataSet.startHour),
                Number(generalMeetingFormDataSet.startMinute)).toString()
        }

        let trimedProposalFormDataSetList = generalMeetingFormDataSet.proposalFormDataSetList
            .filter(proposalByAssocFormDataSet =>
                generalMeetingFormDataSet.selectedAssocIdList.includes(proposalByAssocFormDataSet.assocId))

        if (!isDraft) {
            trimedProposalFormDataSetList = trimedProposalFormDataSetList.map((proposalByAssocFormDataSet) => {
                const list = proposalByAssocFormDataSet.proposalDetailInfoFormDataSetList
                let end = list.length
                while (end > 0 && list[end - 1].resolutionType === null && list[end - 1].proposalName === '') {
                    end--
                }
                return {...proposalByAssocFormDataSet, proposalDetailInfoFormDataSetList: list.slice(0, end),}
            })
        }

        const proposalList: ProposalByAssocRequest[] = trimedProposalFormDataSetList.map(
            proposal => ({
                assocId: proposal.assocId,
                proposalDetailInfoList: proposal.proposalDetailInfoFormDataSetList.map(
                    proposalDetailInfo => ({
                        proposalNumber: proposalDetailInfo.proposalNumber,
                        resolutionType: proposalDetailInfo.resolutionType,
                        proposalName: proposalDetailInfo.proposalName === '' ? null : proposalDetailInfo.proposalName,
                    }),
                ),
            }),
        )

        const fileNameList = generalMeetingFormDataSet.generalMeetingFileList.map(file => file.fileName)

        return {
            generalMeetingName: generalMeetingFormDataSet.generalMeetingName === ''
                ? null
                : generalMeetingFormDataSet.generalMeetingName,
            assocIdList: generalMeetingFormDataSet.selectedAssocIdList,
            generalMeetingDate: AdminGeneralMeetingRegisterPage.formatDateToString(
                generalMeetingFormDataSet.generalMeetingDate),
            startTime,
            votingPreferenceDeadline: AdminGeneralMeetingRegisterPage.formatDateToString(
                generalMeetingFormDataSet.votingPreferenceDeadline),
            questionAvailability: generalMeetingFormDataSet.questionAvailability,
            questionDeadline: AdminGeneralMeetingRegisterPage.formatDateToString(
                generalMeetingFormDataSet.questionDeadline),
            fileNameList,
            proposalList,
            noticeContent: generalMeetingFormDataSet.noticeContent === ''
                ? null
                : generalMeetingFormDataSet.noticeContent,
            releaseDate: AdminGeneralMeetingRegisterPage.formatDateToString(generalMeetingFormDataSet.releaseDate),
        }
    }

    async handleSaveAsDraftOkClick(): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
            navigate,
        } = this.props
        const {generalMeetingFormDataSet} = this.state

        eventEmitter.closeDialog()
        eventEmitter.clearBroadcastedErrors()

        await eventEmitter.showLoading()
        let generalMeetingId
        let initialMessage
        try {
            if (!canSaveAsDraftGeneralMeeting(generalMeetingFormDataSet)) return

            const generalMeeting = this.createGeneralMeetingRequest(generalMeetingFormDataSet, true)

            const generalMeetingNumberAndInformation = await asyncCallFunction(
                adminGeneralMeetingRepo.registerGeneralMeeting.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    true,
                    generalMeeting
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0001.toString('総会')},
            )
            if (!generalMeetingNumberAndInformation) return

            generalMeetingId = generalMeetingNumberAndInformation.details
            initialMessage = await this.uploadGeneralMeetingFiles(
                generalMeetingId,
                generalMeetingFormDataSet.generalMeetingFileList,
                generalMeetingNumberAndInformation.information,
            )
        } finally {
            eventEmitter.closeLoading()
        }

        navigate(
            `/admin/condominiums/${this.props.condominiumCd}/general-meetings/${generalMeetingId}`,
            {state: {initialMessage}},
        )
    }

    async handleRegisterOkClick(): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
            navigate,
        } = this.props

        const {generalMeetingFormDataSet} = this.state

        eventEmitter.closeDialog()
        eventEmitter.clearBroadcastedErrors()

        await eventEmitter.showLoading()
        let generalMeetingId
        let initialMessage
        try {
            if (!canRegisterGeneralMeeting(generalMeetingFormDataSet)) return

            const generalMeeting = this.createGeneralMeetingRequest(generalMeetingFormDataSet, false)

            const generalMeetingNumberAndInformation = await asyncCallFunction(
                adminGeneralMeetingRepo.registerGeneralMeeting.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    false,
                    generalMeeting
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0001.toString('総会')},
            )
            if (!generalMeetingNumberAndInformation) {
                return
            }

            generalMeetingId = generalMeetingNumberAndInformation.details
            initialMessage = await this.uploadGeneralMeetingFiles(
                generalMeetingId,
                generalMeetingFormDataSet.generalMeetingFileList,
                generalMeetingNumberAndInformation.information,
            )

            // 全てファイルのアップロードに成功した場合は、ステータスを下書きから公開予約に更新する
            if (initialMessage.messageLevel !== MESSAGE_TYPE.ERROR) {
                try {
                    // AsyncCallFunctionによる標準の通信エラー処理を行わない
                    // 原因はなんであれ公開予約ではなく、下書き保存になったことをユーザーに伝える必要があるため、全ての例外を捕捉する
                    await adminGeneralMeetingRepo.updateGeneralMeetingStatus(
                        condominiumCd,
                        generalMeetingId,
                        {
                            generalMeetingStatus: GENERAL_MEETING_STATUS.BEFORE_RELEASE.value,
                            version: 1,
                        }
                    )
                } catch (e) {
                    initialMessage = {
                        title: Messages.EGMTA0007.toString(),
                        messageLevel: MESSAGE_TYPE.ERROR,
                        detail: Messages.ESYCC0020.toString(),
                    }
                }
            }
        } finally {
            eventEmitter.closeLoading()
        }

        navigate(
            `/admin/condominiums/${this.props.condominiumCd}/general-meetings/${generalMeetingId}`,
            {state: {initialMessage}},
        )
    }

    async uploadGeneralMeetingFiles(
        generalMeetingId: number,
        fileList: GeneralMeetingFile[],
        information: Information,
    ): Promise<HeadingAreaMessageParams> {
        const fileCount = fileList.length
        const errorFiles: string[] = []
        for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
            this.props.eventEmitter.changeLoadingMessage(
                `ファイルアップロード中... ${fileIndex + 1}/${fileCount}`
            )
            try {
                // AsyncCallFunctionによる標準の通信エラー処理を行わない
                // 原因はなんであれファイルを再登録する必要があることをユーザーに伝える必要があるため、全ての例外を捕捉する
                const file = fileList[fileIndex]
                const fileData: string = file.fileNumber === null ?
                    String(file.fileData).split(',')[1] : ''
                await this.props.adminGeneralMeetingRepo.registerGeneralMeetingFile(
                    this.props.condominiumCd,
                    generalMeetingId,
                    {
                        fileNumber: file.fileNumber,
                        fileName: file.fileName,
                        fileData: fileData,
                        fileSize: file.fileSize,
                    },
                )
            } catch (e) {
                errorFiles.push(fileList[fileIndex].fileName)
            }
        }
        if (errorFiles.length > 0) {
            return {
                title: Messages.EGMTA0007.toString(),
                messageLevel: MESSAGE_TYPE.ERROR,
                detail: '以下のファイル登録に失敗しています。編集画面より再度登録してください。',
                itemErrors: errorFiles,
            }
        }
        return {
            title: information.title ?? null,
            messageLevel: information.type,
            detail: information.message,
        }
    }

    handleCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    handleChangeGeneralMeetingFormDataSet(generalMeetingFormDataSet: GeneralMeetingFormDataSet): void {
        this.setState({generalMeetingFormDataSet})
    }

    render(): ReactNode {
        return <>
            <HeaderArea>
                <AdminDefaultButton
                    onClick={this.handleClickSaveAsDraftButton}
                    disabled={!canSaveAsDraftGeneralMeeting(this.state.generalMeetingFormDataSet)}
                >
                    下書き保存する
                </AdminDefaultButton>
                <AdminPrimaryButton
                    onClick={this.handleRegisterButton}
                    disabled={!canRegisterGeneralMeeting(this.state.generalMeetingFormDataSet)}
                >
                    公開で予約する
                </AdminPrimaryButton>
            </HeaderArea>
            <AdminGeneralMeetingInputForm
                condominiumCd={this.props.condominiumCd}
                assocList={this.state.assocList}
                generalMeetingFormDataSet={this.state.generalMeetingFormDataSet}
                currentDate={this.state.currentDate}
                eventEmitter={this.props.eventEmitter}
                onChangeGeneralMeetingFormDataSet={this.handleChangeGeneralMeetingFormDataSet}
                natFileRepo={this.props.natFileRepo}
                asyncCallFunction={this.props.asyncCallFunction}
                isAfterRelease={false}
            />
            <FooterArea>
                <StyledPageFooterAreaRight>
                    <AdminDefaultButton
                        onClick={this.handleClickSaveAsDraftButton}
                        disabled={!canSaveAsDraftGeneralMeeting(this.state.generalMeetingFormDataSet)}
                    >
                        下書き保存する
                    </AdminDefaultButton>
                    <AdminPrimaryButton
                        onClick={this.handleRegisterButton}
                        disabled={!canRegisterGeneralMeeting(this.state.generalMeetingFormDataSet)}
                    >
                        公開で予約する
                    </AdminPrimaryButton>
                </StyledPageFooterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/general-meetings`}
                    >
                        総会一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </FooterArea>
        </>
    }
}

export default createAdminPage(AdminGeneralMeetingRegisterPage)

const HeaderArea = styled(RightRowFlexContainer)`
  gap: 10px;
  padding-top: 10px;
  padding-bottom: 15px;
`

const FooterArea = styled(RowFlexContainer)`
  justify-content: space-between;
  padding-top: 10px
`

const StyledPageFooterAreaRight = styled(PageFotterAreaRight)`
  gap: 10px;
`
